"use client";

import { useLayoutEffect, useState } from "react";
import { LoaderCircle } from "lucide-react";

import {
  readDisplayedAvatar,
  rememberDisplayedAvatar,
} from "@/lib/account-rail-cache";

/** Native images reuse the browser cache without Radix's mount-time loading fallback. */
export function AccountRailAvatar({
  src,
  alt = "",
  kind,
  fallback,
  bootstrap = false,
  header = false,
  pending = false,
}: {
  src?: string | null;
  alt?: string;
  kind: "user" | "organization";
  fallback: React.ReactNode;
  bootstrap?: boolean;
  header?: boolean;
  pending?: boolean;
}) {
  const displayedSource = pending ? readDisplayedAvatar(kind) || src : src;
  useLayoutEffect(() => {
    if (!pending && !bootstrap) rememberDisplayedAvatar(kind, src);
  }, [kind, src, pending, bootstrap]);
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const showImage = !!displayedSource && failedSource !== displayedSource;
  const organization = kind === "organization";
  return (
    <span
      className={
        header
          ? "relative flex size-6 shrink-0 overflow-hidden rounded-full ring-brand-primary group-focus-visible/account:ring-2 group-data-[state=open]/account:ring-2"
          : `relative flex size-8 shrink-0 overflow-hidden rounded-lg ${organization ? "bg-background p-0.5" : "p-1"}`
      }
    >
      {bootstrap ? (
        <span
          className={`absolute rounded-lg bg-cover bg-center ${organization ? "rail-bootstrap-org-image inset-0.5" : "rail-bootstrap-user-image inset-1"}`}
        />
      ) : showImage ? (
        // A normal img is intentional: don't replace a cached image with a loading icon.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={displayedSource}
          alt={alt}
          className="aspect-square size-full rounded-lg"
          onError={() => setFailedSource(displayedSource ?? null)}
        />
      ) : null}
      {(!showImage || bootstrap) && (
        <span
          className={`flex size-full items-center justify-center rounded-lg bg-background ${bootstrap ? (organization ? "rail-bootstrap-org-fallback" : "rail-bootstrap-user-fallback") : ""}`}
        >
          {fallback}
        </span>
      )}
      {organization && pending && (
        <span
          role="status"
          aria-label="Loading organization"
          className="absolute inset-0 flex items-center justify-center rounded-lg bg-background"
        >
          <LoaderCircle
            aria-hidden="true"
            className="size-4 animate-spin text-brand-primary"
          />
        </span>
      )}
    </span>
  );
}
